import { User } from "../types/Types";
import { isYouthOrEarlyStageMembership } from "./membershipTier";

/**
 * Azure AD / B2C object id from a user DTO. APIs may send `objectId` or `ObjectId`.
 */
export function resolvedObjectId(
  u: Pick<User, "objectId" | "id"> | null | undefined
): string {
  if (!u) return "";
  const r = u as User & { ObjectId?: string };
  return String(r.objectId ?? r.ObjectId ?? "").trim();
}

export function getAvatarUrl(user: User): string {
  return (
    (user as User & { professionalPhoto?: string }).professionalPhoto ||
    user.ProfessionalPhoto ||
    user.PersonalProfessionalPhoto ||
    user.personalProfessionalPhoto ||
    ""
  );
}

export function getInitials(user: User): string {
  const first = (user.firstName || "").trim().charAt(0).toUpperCase();
  const last = (user.lastName || "").trim().charAt(0).toUpperCase();
  return first && last ? `${first}${last}` : first || last || "?";
}

export function getRole(user: User): string {
  if (isYouthOrEarlyStageMembership(user.membershipName))
    return user.studyInterest || "";
  return user.personalPositionCompany || user.businessSectorIndustry || "";
}

export function getMembershipLabel(name: string): string {
  if (name === "Executive Membership") return "Executive Member";
  if (name === "Business Membership") return "Business Member";
  if (name === "Early Stage Membership") return "Early Stage Member";
  if (name === "Youth Membership") return "Youth Member";
  return name;
}

/** i18n key for connections list membership badge (`connections.memberBadge.*`). */
export function getMembershipBadgeKey(
  target: string | ConnectionTarget
): string {
  if (typeof target !== "string") {
    const paidName = resolveMembershipName(target);
    // Paid members: membershipName only (ignore stale Explorer segment).
    if (normalizeMembership(paidName)) {
      return membershipBadgeKeyFromTier(normalizeMembership(paidName));
    }

    // Explorers: same segment → tier mapping as getConnectionMembershipDisplay.
    const segment = resolveExplorerAttendeeType(target);
    if (segment === "18_23_early_entrepreneur_sme") {
      return "connections.memberBadge.earlyStage";
    }
    if (segment === "23_plus_early_entrepreneur_sme") {
      return "connections.memberBadge.business";
    }
    if (segment === "23_plus_established_sme_1million_plus_turnover") {
      return "connections.memberBadge.executive";
    }
  }

  return membershipBadgeKeyFromTier(
    normalizeMembership(typeof target === "string" ? target : resolveMembershipName(target))
  );
}

function membershipBadgeKeyFromTier(
  tier: ReturnType<typeof normalizeMembership>
): string {
  switch (tier) {
    case "Youth Membership":
      return "connections.memberBadge.youth";
    case "Early Stage Membership":
      return "connections.memberBadge.earlyStage";
    case "Business Membership":
      return "connections.memberBadge.business";
    case "Executive Membership":
      return "connections.memberBadge.executive";
    default:
      return "";
  }
}

export type UpgradePromptKind = "executive" | "business";

/** Explorer segmentation values from step 2 (ExplorerSegmentation). */
const EXPLORER_SEGMENT_CONNECTION_LABEL: Record<string, string> = {
  "18_23_early_entrepreneur_sme": "Early Stage Member",
  "23_plus_early_entrepreneur_sme": "Business Member",
  "23_plus_established_sme_1million_plus_turnover": "Executive Member",
};

const EXPLORER_SEGMENT_MEMBERSHIP_TIER: Record<string, string> = {
  "18_23_early_entrepreneur_sme": "Youth Membership",
  "23_plus_early_entrepreneur_sme": "Business Membership",
  "23_plus_established_sme_1million_plus_turnover": "Executive Membership",
};

export type ConnectionTarget = Pick<User, "membershipName" | "explorerAttendeeType">;

export type ExplorerMeForConnection = {
  explorer?: unknown | null;
  payload?: { segment?: string } | null;
};

/** Explorer only: paid membershipName and/or step-2 segmentation tier for connection rules. */
export function buildConnectionTargetFromUser(
  user: ConnectionTarget | null | undefined,
  explorerMe?: ExplorerMeForConnection | null,
  isExplorer = false
): ConnectionTarget {
  const r = user as User & { MembershipName?: string | null };
  const membershipName = String(user?.membershipName ?? r?.MembershipName ?? "").trim();

  if (!isExplorer) {
    return { membershipName, explorerAttendeeType: "" };
  }

  const fromUser = resolveExplorerAttendeeType(user);
  const explorerRow = explorerMe?.explorer as
    | { attendeeType?: string; AttendeeType?: string }
    | null
    | undefined;
  const fromExplorer =
    String(explorerRow?.attendeeType ?? explorerRow?.AttendeeType ?? "").trim() ||
    String(explorerMe?.payload?.segment ?? "").trim();
  return {
    membershipName,
    explorerAttendeeType: fromUser || fromExplorer || "",
  };
}

/**
 * Current user's tier for getConnectionPermission: paid members use membershipName only;
 * Explorers use DDL segmentation (step 2) via buildConnectionTargetFromUser.
 */
export function resolveCurrentUserConnectionSource(
  user: ConnectionTarget | null | undefined,
  explorerMe: ExplorerMeForConnection | null | undefined,
  isExplorer: boolean
): ConnectionTarget | string | undefined {
  if (!user) return undefined;
  if (!isExplorer) return user.membershipName;
  return buildConnectionTargetFromUser(user, explorerMe, true);
}

export function resolveExplorerAttendeeType(
  user: Pick<User, "explorerAttendeeType" | "userCategory"> | null | undefined
): string {
  if (!user) return "";
  const r = user as User & { ExplorerAttendeeType?: string | null };
  return String(r.explorerAttendeeType ?? r.ExplorerAttendeeType ?? "").trim();
}

function resolveMembershipName(
  user: ConnectionTarget | null | undefined
): string {
  if (!user) return "";
  const r = user as User & { MembershipName?: string | null };
  return String(user?.membershipName ?? r?.MembershipName ?? "").trim();
}

/**
 * Connections list badge: paid membershipName first (same as profile);
 * Explorer segment only when there is no paid membership tier.
 */
export function getConnectionMembershipDisplay(
  user: Pick<User, "membershipName" | "explorerAttendeeType" | "userCategory"> | null | undefined
): string {
  const name = resolveMembershipName(user);
  if (normalizeMembership(name)) {
    return getMembershipLabel(name);
  }

  const segment = resolveExplorerAttendeeType(user);
  const fromSegment = segment ? EXPLORER_SEGMENT_CONNECTION_LABEL[segment] : "";
  if (fromSegment) return fromSegment;

  return getMembershipLabel(name);
}

/** Profile curved badge — paid membership first; Explorer segment only as fallback. */
export function getProfileMembershipBadgeTheme(
  user: ConnectionTarget | null | undefined
): { stroke: string; text: string } | null {
  const paidName = resolveMembershipName(user);
  if (!normalizeMembership(paidName)) {
    const segment = resolveExplorerAttendeeType(user);
    // Match connections badge: 18–23 explorer segment → Early Stage, not Youth.
    if (segment === "18_23_early_entrepreneur_sme") {
      return { stroke: "#18c2c6", text: "#EarlyStage" };
    }
  }

  const tier = resolveConnectionTargetMembership(user);
  switch (tier) {
    case "Youth Membership":
      return { stroke: "#18c2c6", text: "#Youth" };
    case "Early Stage Membership":
      return { stroke: "#18c2c6", text: "#EarlyStage" };
    case "Business Membership":
      return { stroke: "#1c57b9", text: "#Business" };
    case "Executive Membership":
      return { stroke: "#544bc1", text: "#Executive" };
    default:
      return null;
  }
}

/**
 * Tier for connection rules / badges: paid membershipName first;
 * Explorer segment only when membership is absent (true unpaid Explorer).
 */
export function resolveConnectionTargetMembership(
  user: ConnectionTarget | null | undefined
): string {
  const membershipName = resolveMembershipName(user);
  if (normalizeMembership(membershipName)) {
    return membershipName;
  }

  const segment = resolveExplorerAttendeeType(user);
  const fromSegment = segment ? EXPLORER_SEGMENT_MEMBERSHIP_TIER[segment] : "";
  if (fromSegment) return fromSegment;

  return membershipName;
}

export function normalizeMembership(
  m: string | undefined
): "Youth Membership" | "Early Stage Membership" | "Business Membership" | "Executive Membership" | "" {
  const s = (m ?? "").trim().toLowerCase();
  if (s.includes("early stage")) return "Early Stage Membership";
  if (s.includes("youth")) return "Youth Membership";
  if (s.includes("business")) return "Business Membership";
  if (s.includes("executive")) return "Executive Membership";
  return "";
}

/** Youth and Early Stage are one connection tier (legacy Youth + new Early Stage). */
function isYouthConnectionTier(
  tier: ReturnType<typeof normalizeMembership>,
): boolean {
  return isYouthOrEarlyStageMembership(tier);
}

/**
 * Connection permission: who can send a connection request to whom.
 * Youth / Early Stage → same tier; Business → Youth + Early Stage + Business; Executive → all.
 * Explorer senders skip tier checks and may request any membership (weekly limits still apply).
 * Explorer targets use paid membership when present, else segmentation tier (step 2).
 */
export function getConnectionPermission(
  current: ConnectionTarget | string | undefined,
  target: ConnectionTarget | string,
  isExplorerCurrentUser = false
): {
  canConnect: boolean;
  upgradeMessage: string;
  upgradeKind: UpgradePromptKind | "none";
} {
  if (isExplorerCurrentUser) {
    return { canConnect: true, upgradeMessage: "", upgradeKind: "none" };
  }

  const targetMembership =
    typeof target === "string"
      ? target
      : resolveConnectionTargetMembership(target);
  const currentMembership =
    typeof current === "string"
      ? current
      : resolveConnectionTargetMembership(current);
  const currentTier = normalizeMembership(currentMembership);
  const targetTier = normalizeMembership(targetMembership);
  let canConnect = false;
  let upgradeMessage = "";
  let upgradeKind: UpgradePromptKind | "none" = "none";

  if (!currentTier || !targetTier) {
    return { canConnect: false, upgradeMessage: "", upgradeKind: "none" };
  }

  if (currentTier === targetTier) canConnect = true;
  else if (isYouthConnectionTier(currentTier) && isYouthConnectionTier(targetTier))
    canConnect = true;
  else if (currentTier === "Executive Membership") canConnect = true;
  else if (currentTier === "Business Membership") {
    if (targetTier === "Youth Membership" || targetTier === "Early Stage Membership")
      canConnect = true;
    else {
      upgradeMessage = "Upgrade to Executive membership";
      upgradeKind = "executive";
    }
  } else if (currentTier === "Youth Membership" || currentTier === "Early Stage Membership") {
    if (targetTier === "Business Membership") {
      upgradeMessage = "Upgrade to Business membership";
      upgradeKind = "business";
    } else if (targetTier === "Executive Membership") {
      upgradeMessage = "Upgrade to Executive membership";
      upgradeKind = "executive";
    }
  }

  return { canConnect, upgradeMessage, upgradeKind };
}